'use client';

/**
 * **The cryptocurrency portfolio — a repeating holdings list, kept whole as a
 * `component` block** ([INVDRAW-12] / VALE-83).
 *
 * Extracted from `CryptocurrencyForm.tsx`'s inline react-hook-form
 * `Controller`-bound holdings editor, rebuilt as a plain controlled
 * value/`onChange` component so it can mount inside a drawer that has no RHF
 * form of its own. The behaviour — add/remove a coin, expand to edit its quantity and
 * four price fields — is unchanged.
 *
 * **No `compact` variant, deliberately** — same call as `[INVDRAW-13]`'s
 * `HINPortfolioForm`: this is a repeating, add/remove list the attribute
 * treatment has no arm for (LF-6311 gap E/F), so it keeps `FormField`'s
 * default `form` chrome rather than restyling a sub-form the rollout hasn't
 * actually converted.
 *
 * **Commit-on-blur, not per keystroke.** Each holding field commits through
 * the drawer's real patch path (`onChange`) only once its input blurs — the
 * local `draft` state is what makes typing responsive without a network
 * write per character. Add/remove are discrete actions and commit
 * immediately.
 */

import { useState } from 'react';
import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/inventory/inventory-form-card/shared/FormField';

export interface CryptoHolding {
  id?: string;
  coinName?: string;
  /** How many coins are held (VALE-1940): the AI proposes it, so it is editable here. */
  quantity?: number;
  sharePriceAtDateOfDeath?: number;
  valueAtDateOfDeath?: number;
  currentPrice?: number;
  currentValue?: number;
}

function generateHoldingId(): string {
  return `holding-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function ensureIds(portfolio: CryptoHolding[]): CryptoHolding[] {
  return portfolio.map((h) => (h.id ? h : { ...h, id: generateHoldingId() }));
}

export interface CryptoPortfolioKeptProps {
  portfolio: CryptoHolding[];
  onChange: (portfolio: CryptoHolding[]) => void;
}

export function CryptoPortfolioKept({
  portfolio,
  onChange
}: CryptoPortfolioKeptProps) {
  const [draft, setDraft] = useState<CryptoHolding[]>(() =>
    ensureIds(portfolio)
  );
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  /*
    Re-seed the local draft whenever the cached portfolio moves under us — a
    sibling row's commit, or this component's own commit settling back
    through the cache. Adjusted during render (React's own pattern for
    "reset state when a prop changes"), not in an effect — the effect form
    would call `setState` synchronously inside an effect body, an extra
    render this avoids.
  */
  const [prevPortfolio, setPrevPortfolio] = useState(portfolio);
  if (portfolio !== prevPortfolio) {
    setPrevPortfolio(portfolio);
    setDraft(ensureIds(portfolio));
  }

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const updateField = (
    id: string,
    field: keyof CryptoHolding,
    value: string | number | undefined
  ) => {
    setDraft((prev) =>
      prev.map((h) => (h.id === id ? { ...h, [field]: value } : h))
    );
  };

  const commit = (next: CryptoHolding[]) => {
    setDraft(next);
    onChange(next);
  };

  const handleAdd = () => {
    const id = generateHoldingId();
    setExpanded((prev) => new Set(prev).add(id));
    commit([...draft, { id, coinName: '', valueAtDateOfDeath: 0 }]);
  };

  const handleRemove = (id: string) => {
    commit(draft.filter((h) => h.id !== id));
    setExpanded((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  // Commits whatever is currently in local draft — the field's own onChange
  // already wrote the keystroke into `draft` above.
  const commitDraft = () => commit(draft);

  return (
    <div className="flex flex-col gap-2">
      {draft.map((holding, index) => {
        const isExpanded = holding.id ? expanded.has(holding.id) : false;

        return (
          <div
            key={holding.id}
            className="bg-muted/70 flex flex-col gap-2 rounded-md p-2"
          >
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => holding.id && toggle(holding.id)}
                className="flex flex-1 items-center gap-2 text-left"
              >
                {isExpanded ? (
                  <ChevronUp className="text-muted-foreground h-3 w-3" />
                ) : (
                  <ChevronDown className="text-muted-foreground h-3 w-3" />
                )}
                <span className="font-medium">
                  {holding.coinName || `Coin ${index + 1}`}
                </span>
              </button>
              <Button
                tone="muted"
                type="button"
                size="icon-xs"
                variant="ghost"
                onClick={() => holding.id && handleRemove(holding.id)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>

            {isExpanded && holding.id && (
              <div className="flex flex-col gap-2 pl-5">
                <div className="grid grid-cols-2 gap-2">
                  <FormField
                    label="Coin name"
                    value={holding.coinName || ''}
                    mode="edit"
                    type="text"
                    placeholder="Add coin name"
                    onChange={(value) =>
                      updateField(holding.id!, 'coinName', value as string)
                    }
                    onBlur={commitDraft}
                  />
                  <FormField
                    label="Quantity"
                    value={holding.quantity ?? ''}
                    mode="edit"
                    type="number"
                    step="any"
                    placeholder="0"
                    onChange={(value) =>
                      updateField(holding.id!, 'quantity', value as number)
                    }
                    onBlur={commitDraft}
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <FormField
                    label="Date of Death Price"
                    value={holding.sharePriceAtDateOfDeath ?? ''}
                    mode="edit"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    currency
                    onChange={(value) =>
                      updateField(
                        holding.id!,
                        'sharePriceAtDateOfDeath',
                        value as number
                      )
                    }
                    onBlur={commitDraft}
                  />
                  <FormField
                    label="Date of Death Value"
                    value={holding.valueAtDateOfDeath ?? ''}
                    mode="edit"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    currency
                    onChange={(value) =>
                      updateField(
                        holding.id!,
                        'valueAtDateOfDeath',
                        value as number
                      )
                    }
                    onBlur={commitDraft}
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <FormField
                    label="Current Price"
                    value={holding.currentPrice ?? ''}
                    mode="edit"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    currency
                    onChange={(value) =>
                      updateField(holding.id!, 'currentPrice', value as number)
                    }
                    onBlur={commitDraft}
                  />
                  <FormField
                    label="Current Value"
                    value={holding.currentValue ?? ''}
                    mode="edit"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    currency
                    onChange={(value) =>
                      updateField(holding.id!, 'currentValue', value as number)
                    }
                    onBlur={commitDraft}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}

      <Button
        size="xs"
        density="compact"
        type="button"
        variant="outline"
        onClick={handleAdd}
        className="self-start"
      >
        {draft.length === 0 ? 'Add Coin' : 'Add Additional Coin'}
      </Button>
    </div>
  );
}
